import { Button, Card, CardAnchor, Conditional } from '@payaca/ambrosia';
import { useQueryClient } from '@tanstack/react-query';
import { format, formatDistance } from 'date-fns';
import { FC, useContext, useEffect, useMemo } from 'react';
import { Link, useParams, useRouteMatch } from 'react-router-dom';

import keyFactory from '@/api/queries/customers/keyFactory';
import useGetCustomerEvents from '@/api/queries/customers/useGetCustomerEvents';
import useGetMe from '@/api/queries/me/useGetMe';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import ScheduleContextProvider, {
  ScheduleContext,
} from '@/ui/components/contextProviders/ScheduleContextProvider';
import { ScheduleWithStickyControls } from '@/ui/components/scheduleDispatchMain/ScheduleWithStickyControls';
import DateTab from '@payaca/components/dateTab/DateTab';

import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { CreateScheduledEventRequestData } from '@payaca/store/scheduledEvents/scheduledEventsTypes';
import { DeepPartial } from '@payaca/utilities/types';
import useGetCustomer from '../../../../api/queries/customers/useGetCustomer';

const CustomerScheduleTab: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const { data: me } = useGetMe();
  const { customer } = useGetCustomer(customerId);

  const initialScheduledEvent: DeepPartial<CreateScheduledEventRequestData> =
    useMemo(() => {
      const initialScheduledEvent: DeepPartial<CreateScheduledEventRequestData> =
        {
          customerIds: customer ? [parseInt(customer?.id)] : [],
          name: customer?.name,
          userAssignments: me?.user.id ? [+me.user.id] : [],
        };

      initialScheduledEvent.contactsToNotify = customer?.primaryContact
        ? [
            {
              type: 'customerContact' as const,
              id: +customer?.primaryContact.id,
              name: customer?.primaryContact.name || undefined,
              emailAddress: customer?.primaryContact.email || undefined,
              telephoneNumber: customer?.primaryContact.phone || undefined,
              description: customer?.primaryContact.description || undefined,
            },
          ]
        : [];

      return initialScheduledEvent;
    }, [me, customer]);

  return (
    <ScheduleContextProvider
      createEventBase={initialScheduledEvent}
      disabledFields={['customerIds']}
      hiddenFields={['dealId', 'customerIds']}
    >
      <CustomerScheduleTabContact />
    </ScheduleContextProvider>
  );
};

const CustomerScheduleTabContact: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const {
    data: customerEventsData,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetCustomerEvents(customerId, {
    sortDirection: 'desc',
  });
  const { url } = useRouteMatch();
  const queryClient = useQueryClient();
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();
  const { listenerSubscribe, listenerUnsubscribe } =
    useContext(ScheduleContext);

  useEffect(() => {
    const invalidateCustomerEventQuery = () => {
      void queryClient.invalidateQueries({
        queryKey: keyFactory.customerEvents(customerId),
      });
    };

    listenerSubscribe(
      'onEventCreated',
      'scheduleTabContent',
      invalidateCustomerEventQuery
    );

    listenerSubscribe(
      'onEventUpdated',
      'scheduleTabContent',
      invalidateCustomerEventQuery
    );

    listenerSubscribe(
      'onEventDeleted',
      'scheduleTabContent',
      invalidateCustomerEventQuery
    );

    return () => {
      listenerUnsubscribe('onEventCreated', 'scheduleTabContent');
      listenerUnsubscribe('onEventUpdated', 'scheduleTabContent');
      listenerUnsubscribe('onEventDeleted', 'scheduleTabContent');
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'listenerUnsubscribe', and 'listenerSubscribe'
  }, [queryClient, customerId]);

  return (
    <div className="@container/scheduletab">
      <div className="@screen-lg/scheduletab:flex-row flex flex-col gap-8">
        <div className="@screen-lg/scheduletab:basis-80 shrink-0 grow-0 space-y-4">
          {customerEventsData?.pages.map((customerData) =>
            customerData.customer?.events.items.map((event) => (
              <CardAnchor as={Link} key={event.id} to={`${url}/${event.id}`}>
                <Card.Body>
                  <div className="flex items-center gap-4">
                    <DateTab date={new Date(event.beginAt)} />

                    <div>
                      <p className="supporting-body">
                        {format(
                          event.beginAt,
                          getInternationalDateFormatByLocale(
                            DateFormats.TODAY,
                            accountRegionPreferences?.locale
                          )
                        )}
                      </p>
                      <h4>{event.name}</h4>
                      <p>
                        {format(event.beginAt, 'HH:mm')} -{' '}
                        {format(event.endAt, 'HH:mm')} (
                        {formatDistance(event.beginAt, event.endAt)})
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </CardAnchor>
            ))
          )}

          <Conditional
            condition={
              customerEventsData?.pages[0] &&
              customerEventsData.pages[0].customer.events.totalCount === 0 &&
              !isLoading
            }
          >
            <>
              <h3>No events</h3>
              <p>This customer {"doesn't"} have any events yet</p>
            </>
          </Conditional>

          <Conditional condition={hasNextPage}>
            <div className="text-center">
              <Button
                size={'sm'}
                variant="secondary"
                onClick={() => fetchNextPage()}
                isProcessing={isFetchingNextPage}
              >
                Load more
              </Button>
            </div>
          </Conditional>
        </div>

        <ScheduleWithStickyControls />
      </div>
    </div>
  );
};

export default CustomerScheduleTab;
